import { useEffect, useState } from "react";
import { Button } from "@/components/base/buttons/button";
import { Badge } from "@/components/base/badges/badges";
import { CheckboxBase } from "@/components/base/checkbox/checkbox";
import { toast } from "@/lib/toast-service";
import { apiClient } from "@/lib/api-client";

interface CompanyTypeInfo {
  id: string;
  label: string;
}

interface CompanyTypesResponse {
  company_types: CompanyTypeInfo[];
}

const NON_MODIFIABLE_MENU_ITEMS = ["controlHub", "settings", "activity"];

function formatMenuItemLabel(key: string): string {
  const labelMap: Record<string, string> = {
    "ask-eve": "Ask Eve",
    controlHub: "Control Hub",
    addressBook: "Address Book",
    dashboard: "Control Hub",
  };

  if (labelMap[key]) {
    return labelMap[key];
  }

  const formatted = key.replace(/([A-Z])/g, " $1").trim();
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

export function MenuConfigTab() {
  const [companyTypes, setCompanyTypes] = useState<CompanyTypeInfo[]>([]);
  const [menuItemKeys, setMenuItemKeys] = useState<string[]>([]);
  const [matrixState, setMatrixState] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const [savingCells, setSavingCells] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadMenuMatrix();
  }, []);

  const loadMenuMatrix = async () => {
    setIsLoading(true);
    try {
      const companyTypesResponse = await apiClient<CompanyTypesResponse>(
        "/menu-configurations/company-types",
        { requireAuth: true }
      );

      const types = companyTypesResponse.company_types;
      setCompanyTypes(types);

      if (types.length === 0) {
        setMenuItemKeys([]);
        setMatrixState({});
        return;
      }

      const typeConfigs = await Promise.all(
        types.map(async (companyType) => {
          const data = await apiClient<{ items: Record<string, boolean> }>(
            `/menu-configurations/by-type/${companyType.id}`,
            { requireAuth: true }
          );

          return {
            companyTypeId: companyType.id,
            items: data.items,
          };
        })
      );

      const allMenuKeys = Array.from(
        new Set([
          ...NON_MODIFIABLE_MENU_ITEMS,
          ...typeConfigs.flatMap((config) => Object.keys(config.items)),
        ])
      );
      const sortedKeys = allMenuKeys.sort((a, b) =>
        formatMenuItemLabel(a).localeCompare(formatMenuItemLabel(b))
      );

      const nextMatrix: Record<string, Record<string, boolean>> = {};
      for (const menuKey of sortedKeys) {
        nextMatrix[menuKey] = {};
        for (const companyType of types) {
          const itemsForType =
            typeConfigs.find((c) => c.companyTypeId === companyType.id)?.items ??
            {};
          const enabled = NON_MODIFIABLE_MENU_ITEMS.includes(menuKey)
            ? true
            : itemsForType[menuKey] !== false;
          nextMatrix[menuKey][companyType.id] = enabled;
        }
      }

      setMenuItemKeys(sortedKeys);
      setMatrixState(nextMatrix);
    } catch (error) {
      console.error("Error loading menu matrix:", error);
      toast.error("Failed to load menu configuration matrix");
      setMenuItemKeys([]);
      setMatrixState({});
    } finally {
      setIsLoading(false);
    }
  };

  const isCellSaving = (menuKey: string, companyTypeId: string) =>
    savingCells[`${menuKey}:${companyTypeId}`] === true;

  const updateCell = async (menuKey: string, companyTypeId: string) => {
    if (NON_MODIFIABLE_MENU_ITEMS.includes(menuKey)) return;

    const cellId = `${menuKey}:${companyTypeId}`;
    if (isCellSaving(menuKey, companyTypeId)) return;

    const previousValue = matrixState[menuKey]?.[companyTypeId] ?? true;
    const nextValue = !previousValue;

    const companyTypeItems = menuItemKeys.reduce<Record<string, boolean>>(
      (acc, key) => {
        if (NON_MODIFIABLE_MENU_ITEMS.includes(key)) {
          acc[key] = true;
        } else if (key === menuKey) {
          acc[key] = nextValue;
        } else {
          acc[key] = matrixState[key]?.[companyTypeId] ?? true;
        }
        return acc;
      },
      {}
    );

    setMatrixState((prev) => ({
      ...prev,
      [menuKey]: {
        ...prev[menuKey],
        [companyTypeId]: nextValue,
      },
    }));
    setSavingCells((prev) => ({ ...prev, [cellId]: true }));

    try {
      await apiClient<{ items: Record<string, boolean> }>(
        "/menu-configurations/global",
        {
          method: "POST",
          requireAuth: true,
          body: JSON.stringify({
            company_type: companyTypeId,
            items: companyTypeItems,
          }),
        }
      );

      toast.success("Menu section updated");
    } catch (error) {
      console.error("Error saving menu configuration:", error);
      toast.error("Failed to save menu section");
      setMatrixState((prev) => ({
        ...prev,
        [menuKey]: {
          ...prev[menuKey],
          [companyTypeId]: previousValue,
        },
      }));
    } finally {
      setSavingCells((prev) => {
        const next = { ...prev };
        delete next[cellId];
        return next;
      });
    }
  };

  const hasData = companyTypes.length > 0 && menuItemKeys.length > 0;

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-secondary bg-primary p-6">
        <h3 className="text-lg font-semibold text-primary mb-1">
          Global Menu Configuration
        </h3>
        <p className="text-tertiary text-sm mb-6">
          Configure menu sections by company type using the matrix below. Changes
          are applied immediately.
        </p>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-tertiary">Loading matrix...</div>
          </div>
        ) : !hasData ? (
          <div className="text-center py-8 text-tertiary">
            No menu configuration data available
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-xs text-tertiary">
              Sections marked as <strong>Always Enabled</strong> cannot be disabled.
            </p>

            <div className="border border-secondary rounded-lg overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-secondary">
                    <th
                      scope="col"
                      className="text-left text-xs text-tertiary font-medium uppercase px-4 py-2.5 min-w-[220px]"
                    >
                      Menu section
                    </th>
                    {companyTypes.map((companyType) => (
                      <th
                        key={companyType.id}
                        scope="col"
                        className="text-center text-xs text-tertiary font-medium uppercase px-4 py-2.5 min-w-[160px]"
                      >
                        {companyType.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {menuItemKeys.map((menuKey, index) => {
                    const isLocked = NON_MODIFIABLE_MENU_ITEMS.includes(menuKey);
                    return (
                      <tr
                        key={menuKey}
                        className={index % 2 === 0 ? "bg-primary" : "bg-secondary/20"}
                      >
                        <th
                          scope="row"
                          className="px-4 py-2 text-left align-middle"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-primary">
                              {formatMenuItemLabel(menuKey)}
                            </span>
                            {isLocked && (
                              <Badge color="gray" size="sm">
                                Always Enabled
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-tertiary">Key: {menuKey}</p>
                        </th>
                        {companyTypes.map((companyType) => {
                          const isSaving = isCellSaving(menuKey, companyType.id);
                          const isDisabled = isLocked || isSaving;
                          const isSelected =
                            matrixState[menuKey]?.[companyType.id] ?? true;

                          return (
                            <td
                              key={`${menuKey}:${companyType.id}`}
                              className="px-4 py-2 text-center align-middle"
                            >
                              <button
                                type="button"
                                onClick={() => updateCell(menuKey, companyType.id)}
                                disabled={isDisabled}
                                className="focus:outline-none disabled:cursor-not-allowed"
                                aria-label={`${isSelected ? "Disable" : "Enable"} ${formatMenuItemLabel(menuKey)} for ${companyType.label}`}
                              >
                                <CheckboxBase
                                  isSelected={isSelected}
                                  isDisabled={isDisabled}
                                />
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-lg border border-secondary bg-primary p-6">
        <h3 className="text-lg font-semibold text-primary mb-1">
          Company-Specific Overrides
        </h3>
        <p className="text-tertiary text-sm mb-4">
          To configure menu overrides for specific companies (e.g., demo
          accounts, beta testers), edit the company directly from the Companies
          page.
        </p>
        <Button
          color="secondary"
          onClick={() => (window.location.href = "/admin/companies")}
          size="md"
        >
          Go to Companies
        </Button>
      </div>
    </div>
  );
}
